import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { type AuthClient } from "~/api/AuthClient";
import { type AuthUser } from "~/api/auth/AuthUser";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useTracking } from "~/hooks/useTracking";

type AuthContextValue = {
  user: AuthUser;
  authClient: AuthClient;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

type Props = {
  authClient: AuthClient;
  children: React.ReactNode;
};

export const AuthProvider = ({ authClient, children }: Props) => {
  const { identifyUser } = useTracking();
  const { updateUserFlags } = useFeatureFlags();

  const [user, setUser] = useState<AuthUser>(authClient.user);

  const onUserChange = useCallback(
    (current: AuthUser) => {
      // NOTE: order here is important.
      identifyUser(current);
      updateUserFlags(current);
      setUser(current);
    },
    [setUser, updateUserFlags, identifyUser],
  );

  // subscribe to AuthClient events
  useEffect(() => {
    authClient.on("stateChange", onUserChange);

    return () => {
      authClient.off("stateChange", onUserChange);
    };
  }, []);

  // combine reactive state with authClient for Provider
  const combinedState = useMemo(
    () => ({
      user,
      authClient,
    }),
    [user],
  );

  return (
    <AuthContext.Provider value={combinedState}>
      {children}
    </AuthContext.Provider>
  );
};
